import * as types from './mutations-types';
import { get, set } from 'lodash-es';
import { clientApi } from '@phoenix/clientApi';

const initialState = {
  orders: [],
  countries: [],
  groups: {},
  ordersLineData: [],
  orderReturnExchangeAddress: {},
  isLoading: false,
  onOrderPage: false,
  selectedOrderToken: null,
  refundReasons: null,
  exchangeReasons: null,
  isEditingReturnExchangeAddress: false,
};

const getters = {
  openOrders: (state) => (state.orders ? state.orders.filter((order) => order.isOpen) : []),

  closedOrders: (state) => (state.orders ? state.orders.filter((order) => !order.isOpen) : []),

  getOrder: (state) => (number) => state.orders.find((order) => String(order.number) === String(number)),

  getOrderByToken: (state) => (token) => state.orders.find((order) => String(order.token) === String(token)),

  selectedOrder: (state) => state.orders.find((order) => String(order.token) === String(state.selectedOrderToken)),

  getOrderIndex: (state) => (token) => state.orders.findIndex((order) => String(order.token) === String(token)),

  unkwPaymentTypes: (state, getters) => {
    const unkwPayment = getters.selectedOrder
      ? getters.selectedOrder.payments.find((payment) => payment.typePayment === 'UNKW')
      : null;
    return unkwPayment ? unkwPayment.paymentTypes : [];
  },

  getOrderLineData: (state) => (number) => state.ordersLineData[number] ?? [],

  getSelectedOrderLineData: (state, getters) => getters.getOrderLineData(getters.selectedOrder.number) ?? [],

  canReturnExchangeFormSubmit: (state, getters) =>
    getters.getSelectedOrderLineData.filter(
      (ele) => ele.typeOfReturn !== '' && ele.typeOfReturn !== null && ele.reason !== '' && ele.reason !== null,
    ).length > 0,
};

const actions = {
  async fetchOrders({ commit, dispatch }) {
    try {
      commit(types.MY_ACCOUNT_ORDERS_SET_LOADING, true);

      const { data } = await clientApi.get(window.route('api.customer.order.index'));
      dispatch('setOrders', data.data);
      commit(types.MY_ACCOUNT_ORDERS_SET_LOADING, false);
    } catch (error) {
      window.Phoenix.sentry.captureException(error);
      window.Phoenix.toaster.notify({
        type: 'danger',
        text: error.response.data.message,
      });

      commit(types.MY_ACCOUNT_ORDERS_SET_LOADING, false);
    }
  },

  async fetchDefaultDeliveryAddress({ dispatch, getters }) {
    let defaultAddress = {};
    defaultAddress = { ...getters.selectedOrder.delivery.fullAddress };
    delete defaultAddress.default;
    delete defaultAddress.label;
    delete defaultAddress.addCountry;

    dispatch('setReturnExchangeAddress', defaultAddress);
  },

  setOrdersLineData({ commit }, orders) {
    const ordersLineData = {};
    orders.forEach((order) => {
      ordersLineData[order.number] = [];
      order.lines.forEach((line) => {
        ordersLineData[order.number].push({
          id: line.id,
          typeOfReturn: null,
          reason: null,
        });
      });
    });

    commit(types.MY_ACCOUNT_SET_ORDERS_LINE_DATA, ordersLineData);
  },

  setOrders({ commit, dispatch }, orders) {
    commit(types.MY_ACCOUNT_ORDERS_SET_ORDERS, orders);
    dispatch('setOrdersLineData', orders);
  },

  setOrderLoading({ commit }, status) {
    commit(types.MY_ACCOUNT_ORDERS_SET_LOADING, status);
  },

  setSelectedOrder({ commit }, token) {
    commit(types.MY_ACCOUNT_ORDERS_SET_SELECTED, token);
  },

  async refreshOrder({ getters, commit }, token) {
    try {
      const { data } = await clientApi.get(
        window.route('api.customer.order.show', {
          token,
        }),
      );
      commit(types.MY_ACCOUNT_ORDERS_UPDATE_ORDER, {
        index: getters.getOrderIndex(token),
        order: data.order,
      });
    } catch (error) {
      window.Phoenix.sentry.captureException(error);
    }
  },

  refreshUntilPaymentIsDone({ state, getters, dispatch }, token = null) {
    if (token === null) {
      token = state.selectedOrderToken;
    }

    setTimeout(async () => {
      await dispatch('refreshOrder', token);
      const order = getters.getOrderByToken(token);
      if (order.isPaymentPending) {
        dispatch('refreshUntilPaymentIsDone', token);
      }
    }, 5000);
  },

  async cancelOrder({ dispatch }, token) {
    try {
      const response = await clientApi.post(window.route('api.customer.order.cancel', { token }));

      await dispatch('refreshOrder', token);

      if (get(response, ['data', 'message'], '') !== '') {
        window.Phoenix.toaster.notify({
          type: 'success',
          text: response.data.message,
        });
      }
    } catch (error) {
      window.Phoenix.sentry.captureException(error);
      window.Phoenix.toaster.notify({
        type: 'danger',
        text: error.response.data.message,
      });
    }
  },

  async returnExchangeOrder({ dispatch, state, getters }) {
    const address = state.orderReturnExchangeAddress;
    const orderLineData = getters.getSelectedOrderLineData;
    const token = state.selectedOrderToken;

    try {
      await clientApi.post(
        window.route('api.customer.order.exchange', {
          token,
        }),
        {
          address,
          orderLineData,
        },
      );

      window.Phoenix.toaster.notify({
        type: 'success',
        text: window.Phoenix.i18n.t('Order.returnExchange.success'),
      });
    } catch (error) {
      window.Phoenix.sentry.captureException(error);
      window.Phoenix.toaster.notify({
        type: 'danger',
        text: error.response.data.message,
      });
    }

    dispatch('setOrdersLineData', state.orders);
    dispatch('refreshOrder', token);
  },

  updateOrderLineData({ commit, getters }, { lineDataId, data }) {
    const currentOrder = getters.selectedOrder;
    const orderLineData = [...getters.getSelectedOrderLineData];
    const orderLineIndex = orderLineData.findIndex((line) => line.id === lineDataId);

    orderLineData[orderLineIndex] = { ...orderLineData[orderLineIndex], ...data };
    commit(types.MY_ACCOUNT_UPDATE_ORDER_LINE_DATA, {
      number: currentOrder.number,
      data: orderLineData,
    });
  },

  setReturnExchangeAddress({ commit }, address) {
    commit(types.MY_ACCOUNT_RETURNEXCHANGE_ADDRESS_SET, address);
    commit(`customerAddressForm/${types.CUSTOMER_ADDRESS_FORM_SET_ADDRESS_IN_FORM}`, address, {
      root: true,
    });
  },

  initOrdersAddressCountriesAndGroups({ commit }, { countries, groups }) {
    commit(types.MY_ACCOUNT_ORDERS_SET_COUNTRIES, countries);
    commit(types.MY_ACCOUNT_ORDERS_SET_GROUPS, groups);
  },

  async fetchRefundAndExchangeReason({ state, commit }) {
    if (state.refundReasons && state.exchangeReasons) {
      return;
    }

    try {
      const refundReasons = await clientApi.get(window.route('api.customer.order.reasons.refund'));
      const exchangeReasons = await clientApi.get(window.route('api.customer.order.reasons.exchange'));

      commit(types.MY_ACCOUNT_SET_REFUND_EXCHANGE_REASON, {
        refundReasons: refundReasons.data.data,
        exchangeReasons: exchangeReasons.data.data,
      });
    } catch (error) {
      window.Phoenix.sentry.captureException(error);
    }
  },
};

const mutations = {
  [types.MY_ACCOUNT_ORDERS_SET_LOADING](state, isLoading) {
    state.isLoading = isLoading;
  },

  [types.MY_ACCOUNT_ORDERS_SET_ORDERS](state, data) {
    state.orders = data;
  },

  [types.MY_ACCOUNT_ORDERS_SET_SELECTED](state, token) {
    state.selectedOrderToken = token;
  },

  [types.MY_ACCOUNT_ORDERS_UPDATE_ORDER](state, { index, order }) {
    set(state, ['orders', index], order);
  },

  [types.MY_ACCOUNT_SET_ORDERS_LINE_DATA](state, data) {
    state.ordersLineData = data;
  },

  [types.MY_ACCOUNT_UPDATE_ORDER_LINE_DATA](state, { number, data }) {
    set(state.ordersLineData, number, data);
  },

  [types.MY_ACCOUNT_RETURNEXCHANGE_ADDRESS_SET](state, address) {
    state.orderReturnExchangeAddress = { ...address };
  },

  [types.MY_ACCOUNT_ORDERS_SET_COUNTRIES](state, countries) {
    state.countries = countries;
  },

  [types.MY_ACCOUNT_ORDERS_SET_GROUPS](state, groups) {
    state.groups = groups;
  },

  [types.MY_ACCOUNT_SET_REFUND_EXCHANGE_REASON](state, reasons) {
    state.refundReasons = reasons.refundReasons;
    state.exchangeReasons = reasons.exchangeReasons;
  },

  [types.MY_ACCOUNT_RETURN_EXCHANGE_IS_EDITING_ADDRESS](state, isEditing) {
    state.isEditingReturnExchangeAddress = isEditing;
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
